import { useState } from 'react';
import { menuIcon } from '../../../../../configs/Icons';
import { useAuth } from '../../../../Contexts/AuthProvider';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import InstOption from '../../../../utils/InstOption';
import {
    CameraIcon,
    ImageUploadingIcon,
    OptionTransparentIcon,
    ReloadGrIcon
} from '../../../../utils/SVGIcon';
import { TextCapitalize } from '../../../../utils/TextFormatter';
import { useAdminListInfo } from '../../AdminList/AdminsListProvider';
import { ApiChangeProfileLogo } from '../../AdminsPageOperation';
import AdminInfoCard from './AdminInfoCard';

function ButtonWrapper({ children, className, onClick }) {
    return (
        <span className="h-full">
            <button
                type="button"
                className={`h-[34px] block text-sm font-e text-secondary bg-[rgba(255,255,255,0.80)] backdrop-blur-[2px] rounded-5 ${className}`}
                onClick={onClick}
            >
                {children}
            </button>
        </span>
    );
}

function ActionArea({
    isStatusActive,
    handleReload,
    handleCustomerStatusModal,
    handleShowEditModal,
    showMenu,
    setShowMenu,
    data
}) {
    return (
        <div className="flex items-center gap-x-2.5 absolute top-2 right-2">
            <div className="flex items-center gap-x-2.5">
                <ButtonWrapper className="w-[34px] p-1" onClick={handleReload}>
                    <ReloadGrIcon />
                </ButtonWrapper>
                <div className="flex flex-row w-full justify-end relative">
                    <ButtonWrapper className="w-[34px]" onClick={() => setShowMenu(!showMenu)}>
                        <span className="w-full block">
                            <OptionTransparentIcon />
                        </span>
                    </ButtonWrapper>

                    <InstOption showMenu={showMenu} setShowMenu={setShowMenu}>
                        {/* Edit Customer info */}
                        {isStatusActive && (
                            <button type="button" onClick={(ev) => handleShowEditModal(ev, data)}>
                                Edit Info
                            </button>
                        )}

                        {/* Change Customer Status */}
                        <button type="button" onClick={(ev) => handleCustomerStatusModal(ev, data)}>
                            {isStatusActive ? 'Make Inactive' : 'Make Active'}
                        </button>
                    </InstOption>
                </div>
            </div>
        </div>
    );
}

function ProductDetails({ customerInfo }) {
    const { setInfoReloadKey } = useAdminListInfo();
    const { id, photoUrl, role } = customerInfo;
    const { setMessage } = useTemplate();
    const { authStateUpdated } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const handleUpload = (e) => {
        if (!e.target.files[0]) return;
        setIsLoading(true);
        const formData = new FormData();
        formData.append('profileImage', e.target.files[0]);
        ApiChangeProfileLogo(id, formData)
            .then(({ title }) => {
                setMessage({ type: 'success', text: title });
                authStateUpdated();
                setInfoReloadKey((prev) => prev + 1);
                setIsLoading(false);
            })
            .catch((err) => {
                setMessage({ type: 'error', text: err?.toString(0) });
                setIsLoading(false);
            });
    };

    const isCustomerInactive = customerInfo?.status === 'inactive';

    return (
        <div className="">
            <div className="size-[100px] bg-[#E6F6ED] relative rounded-full mb-4">
                <span className="w-full h-full overflow-hidden block rounded-full">
                    {isLoading ? (
                        <ImageUploadingIcon />
                    ) : (
                        <img
                            src={photoUrl || menuIcon.settingsWL}
                            alt=""
                            className="w-full h-full block object-cover object-center"
                        />
                    )}
                </span>
                <label
                    type="button"
                    className={`absolute bottom-0 right-0 size-[31px] p-[7px] text-primary bg-white rounded-full cursor-pointer ${
                        isCustomerInactive ? 'invisible' : ''
                    }`}
                >
                    <input
                        type="file"
                        className="hidden"
                        onChange={handleUpload}
                        accept="image/png, image/jpeg, image/jpg"
                    />
                    <CameraIcon />
                </label>
            </div>
            <div className="flex flex-col ite gap-1">
                <div className="flex items-center gap-2.5 relative text-primary text-2xl font-medium whitespace-nowrap  text-ellipsis">
                    <span> {customerInfo?.fullName || '----'}</span>

                    {customerInfo?.currency ? (
                        <span className="bg-[#FFA016] text-sm text-white uppercase py-[3px] px-2.5 rounded-[4px]">
                            {TextCapitalize(customerInfo?.currency) || '--'}
                        </span>
                    ) : null}
                </div>
                <p className="text-sm text-primary">
                    <span>{customerInfo?.primaryEmail || '---'}</span>
                </p>
                <span>
                    <b>{TextCapitalize(role)}</b>
                </span>
            </div>
        </div>
    );
}

function AdminInfo({ data, handleReload }) {
    const [showMenu, setShowMenu] = useState(false);

    const isStatusActive = data?.status === 'active';

    const { handleCustomerStatusModal, handleShowEditModal } = useAdminListInfo();

    return (
        <div
            className={`customer-info-bg relative w-full overflow-hidden h-auto 2xl:max-h-[250px] min-h-[250px] p-2.5 mb-4 2xl:flex justify-between 2xl:gap-3 ${
                data?.demo ? 'placeholder-mode' : ''
            }`}
        >
            {/* product information */}
            <div className="flex flex-1 items-center pl-5 relative z-20 mb-9">
                <ProductDetails customerInfo={data} />
            </div>

            {/* admin info in separate cards */}
            <div className="flex items-end">
                <AdminInfoCard data={data} />
            </div>

            {/* section action buttons */}
            <ActionArea
                isStatusActive={isStatusActive}
                handleReload={handleReload}
                handleCustomerStatusModal={handleCustomerStatusModal}
                handleShowEditModal={handleShowEditModal}
                showMenu={showMenu}
                setShowMenu={setShowMenu}
                data={data}
            />
        </div>
    );
}

export default AdminInfo;
